import type { TFunction } from "i18next";
import { simpleDate } from "@/utils/datetime";
import {
  COMPARE_PERIODS,
  type ComparePeriod,
  type CompareToSelection
} from "./types";

const TRANSLATION_KEY = "datatoolbar.display.compareTo";

/** Order the presets are listed in, with the custom entry last. */
export const COMPARE_PERIOD_OPTIONS: readonly ComparePeriod[] =
  COMPARE_PERIODS;

const MONTHS_IN_YEAR = 12;
const MONTHS_IN_QUARTER = 3;

const atStartOfDay = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * Resolves a preset to the date the comparison runs from, mirroring
 * `resolve_period_start` in contractsapi.
 *
 * The backend is the authority — its `periodStart` comes back on every
 * response. This exists because the picker has to show all five presets'
 * ranges at once, and asking the server for five period starts to populate a
 * dropdown is five requests to render a menu. The rules are pinned by
 * `periods.test.ts` against the backend's documented behaviour, and any drift
 * is visible because the column header tooltip reports the server's answer.
 *
 * `fiscalYearStartMonth` is a 0-indexed month, as `Date#getMonth` gives it.
 * MTD is a plain calendar month on the backend and so is not fiscal here;
 * only QTD and YTD follow the account's fiscal calendar.
 */
export const resolvePeriodStart = (
  period: ComparePeriod,
  fiscalYearStartMonth: number,
  today: Date = new Date()
): Date | null => {
  const reference = atStartOfDay(today);
  const fiscalStart = ((fiscalYearStartMonth % MONTHS_IN_YEAR) +
    MONTHS_IN_YEAR) %
    MONTHS_IN_YEAR;

  switch (period) {
    case "last30Days": {
      const start = new Date(reference);
      start.setDate(start.getDate() - 30);
      return start;
    }
    case "last12Months": {
      const start = new Date(reference);
      start.setFullYear(start.getFullYear() - 1);
      return start;
    }
    case "mtd":
      return new Date(reference.getFullYear(), reference.getMonth(), 1);
    case "qtd": {
      // Quarters are counted from the fiscal year's first month, so the
      // boundary walks with the setting rather than sitting on Jan/Apr/Jul/Oct.
      const monthsIntoFiscalYear =
        (reference.getMonth() - fiscalStart + MONTHS_IN_YEAR) % MONTHS_IN_YEAR;
      const monthsIntoQuarter = monthsIntoFiscalYear % MONTHS_IN_QUARTER;
      return new Date(
        reference.getFullYear(),
        reference.getMonth() - monthsIntoQuarter,
        1
      );
    }
    case "ytd": {
      // Before the fiscal start month, the current fiscal year began last
      // calendar year.
      const year =
        reference.getMonth() < fiscalStart
          ? reference.getFullYear() - 1
          : reference.getFullYear();
      return new Date(year, fiscalStart, 1);
    }
    case "custom":
      // Only the user's own pick can answer this.
      return null;
  }
};

/**
 * The date range a preset covers, for the hint beside it in the picker. The
 * end is always today, because the comparison's "current" side is live.
 */
export const formatPeriodRange = (
  period: ComparePeriod,
  fiscalYearStartMonth: number,
  today: Date = new Date()
): string => {
  const start = resolvePeriodStart(period, fiscalYearStartMonth, today);
  if (!start) return "";
  return `${simpleDate(start)} - ${simpleDate(atStartOfDay(today))}`;
};

/**
 * What a Δ column header puts in its parentheses: the preset's name, or the
 * literal range for a custom selection.
 */
export const formatTimeframeLabel = (
  selection: CompareToSelection,
  t: TFunction
): string => {
  if (selection.period !== "custom") {
    return t(`${TRANSLATION_KEY}.presets.${selection.period}`);
  }
  if (!selection.customStart) {
    return t(`${TRANSLATION_KEY}.presets.custom`);
  }
  // An open-ended label, carrying no end date at all.
  //
  // Only `customStart` is sent, and the endpoint compares against live values,
  // so the window always closes at the present. Naming an end was wrong twice
  // over: `customEnd` stated a range the figures never covered, and `new Date()`
  // stated one that silently aged — the label is computed once and memoized
  // while the comparison it describes keeps moving. No date this header can
  // name as its end stays true, so it names none.
  //
  // `customEnd` stays on the selection because the date picker re-opens on it.
  return t(`${TRANSLATION_KEY}.presets.customSince`, {
    date: simpleDate(selection.customStart)
  });
};

/**
 * Whether a selection is complete enough to send. A custom period with no
 * start would be rejected by the API with a 400.
 */
export const isSelectionSendable = (
  selection: CompareToSelection | undefined
): selection is CompareToSelection =>
  !!selection && (selection.period !== "custom" || !!selection.customStart);
